import {
  createContext,
  JSX,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { NotificationContext } from './notificationContext';
import { AccountContext } from './accountContext';
import { useRequest } from 'ahooks';
import { attachIban, deleteIban, replaceIban } from '@/lib/api/methods';

type IbanProps = {
  ibanNumber: string;
};

export type ContextProps = {
  iban: IbanProps;
  isIbanModalOpen: boolean;
  isAttachIbanLoading: boolean;
  isDeleteIbanLoading: boolean;
  isReplaceIbanLoading: boolean;
  isReplaceIbanRef: React.RefObject<boolean>;

  isDefaultPayout: boolean;
  onDefaultIbanPayout: (isDefault: boolean) => void;

  onReplaceIban: () => void;
  deleteNewOld: () => Promise<any>;
  attachNewIban: (iban: string) => Promise<any>;
  changeIsOpenedModal: (isOpen: boolean) => void;
  replaceNewIban: (iban: string) => Promise<any>;
};

export const IbanContext = createContext({} as ContextProps);

export const IbanProvider = ({ children }: { children: JSX.Element }) => {
  const { openNotificationWithIcon } = useContext(NotificationContext);
  const [isIbanModalOpen, setIsIbanModalOpen] = useState(false);
  const [iban, setIban] = useState<IbanProps>(null);
  const [isDefaultPayout, setIsDefaultPayout] = useState(false);
  const isReplaceIbanRef = useRef(false);
  const {
    refreshPaymentMethods,
    userPaymentMethods,
    statusOfPaymentMethods: { refresh: refreshStatuses },
    isAuthorized,
  } = useContext(AccountContext);

  useEffect(() => {
    if (userPaymentMethods?.iban) {
      setIban({
        ibanNumber: userPaymentMethods.iban.iban_last4,
      });
    } else {
      setIban(null);
    }
  }, [userPaymentMethods]);

  const updateAll = async () => {
    await refreshPaymentMethods();
    refreshStatuses();
    setIsIbanModalOpen(false);
  };

  const onDefaultIbanPayout = (isDefault: boolean) => {
    setIsDefaultPayout(isDefault);
  };

  const { loading: isAttachIbanLoading, runAsync: attachNewIban } = useRequest(
    (iban: string) => attachIban(iban),
    {
      manual: true,
      refreshDeps: [],
      ready: isAuthorized,
      onError: (error: any) => {
        openNotificationWithIcon(
          'error',
          error?.response?.data?.message || error.message
        );
      },
      onSuccess: updateAll,
    }
  );

  const { loading: isReplaceIbanLoading, runAsync: replaceNewIban } =
    useRequest((iban: string) => replaceIban(iban), {
      manual: true,
      refreshDeps: [],
      ready: isAuthorized,
      onError: (error: any) => {
        openNotificationWithIcon(
          'error',
          error?.response?.data?.message || error.message
        );
      },
      onSuccess: updateAll,
    });

  const { loading: isDeleteIbanLoading, runAsync: deleteNewOld } = useRequest(
    () => deleteIban(),
    {
      manual: true,
      refreshDeps: [],
      ready: isAuthorized,
      onError: (error: any) => {
        openNotificationWithIcon(
          'error',
          error?.response?.data?.message || error.message
        );
      },
      onSuccess: updateAll,
    }
  );

  const onReplaceIban = useCallback(() => {
    isReplaceIbanRef.current = true;
    setIsIbanModalOpen(true);
  }, []);

  const changeIsOpenedModal = (isOpen: boolean) => {
    setIsIbanModalOpen(isOpen);
  };
  return (
    <IbanContext.Provider
      value={{
        iban,
        isReplaceIbanRef,
        isIbanModalOpen,
        isDeleteIbanLoading,
        isAttachIbanLoading,
        isReplaceIbanLoading,
        isDefaultPayout,
        deleteNewOld,
        onReplaceIban,
        attachNewIban,
        replaceNewIban,
        changeIsOpenedModal,
        onDefaultIbanPayout,
      }}
    >
      {children}
    </IbanContext.Provider>
  );
};
